// src/components/HabitAnalyzer.js
import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  Typography,
  CircularProgress,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { format, parseISO, isValid } from 'date-fns';
import { it } from 'date-fns/locale';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase';

// Funzione per calcolare la deviazione standard (nessuna modifica)
const calculateStdDev = (values, mean) => {
  if (values.length === 0) return 0;
  const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / values.length;
  return Math.sqrt(variance);
};

const HabitAnalyzer = ({ userFamily, year, month }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState({
    categories: [],
    monthlyTotal: 0,
    averageExpense: 0,
    stdDev: 0,
    maxExpense: 0,
    frequentDays: [],
    recommendations: [],
    trends: []
  });

  // Funzione per generare raccomandazioni basate sui dati (nessuna modifica)
  const generateRecommendations = (categoryData, totalAmount, transactions, avg, stdDev, maxExpense) => {
    const recommendations = [];
    const numTransactions = transactions.length;

    Object.entries(categoryData).forEach(([category, { count, amount }]) => {
      const percentage = (amount / totalAmount) * 100;
      if (percentage > 40) {
        recommendations.push(`La categoria "${category}" rappresenta il ${percentage.toFixed(1)}% delle tue spese totali. Considera di rivedere o ridurre queste spese.`);
      } else if (percentage > 25) {
        recommendations.push(`Le spese per "${category}" sono significative (${percentage.toFixed(1)}%). Valuta se puoi ottimizzarle.`);
      }
      if (count > numTransactions / 3) {
        recommendations.push(`Hai molte transazioni nella categoria "${category}". Una pianificazione più accurata potrebbe aiutarti a contenere le spese.`);
      }
    });

    if (numTransactions > 15) {
      recommendations.push(`Hai effettuato ${numTransactions} transazioni questo mese. Un numero elevato di transazioni potrebbe indicare spese impulsive.`);
    }
    if (stdDev > avg * 0.5) {
      recommendations.push(`L'ampia variabilità delle spese (${stdDev.toFixed(2)}) suggerisce che potresti beneficiare di una maggiore pianificazione finanziaria.`);
    }
    if (maxExpense > avg * 2) {
      recommendations.push(`Una delle tue transazioni ha un importo molto elevato (€${maxExpense.toFixed(2)}). Valuta la possibilità di suddividerla o ripensarla.`);
    }
    if (totalAmount > 0 && totalAmount < 50) {
      recommendations.push(`Il totale mensile (€${totalAmount.toFixed(2)}) è piuttosto basso. Considera se stai investendo abbastanza nella tua qualità di vita o nel benessere.`);
    }
    return recommendations;
  };

    // Funzione per analizzare le abitudini (nessuna modifica)
    const analyzeHabits = useCallback(async (currentUser) => {
        if (!currentUser) return;

        try {
            const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
            const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59);

            const expensesQuery = userFamily && userFamily.id
                ? query(
                    collection(db, 'expenses'),
                    where('familyId', '==', userFamily.id),
                    where('date', '>=', startDate),
                    where('date', '<=', endDate)
                )
                : query(
                    collection(db, 'expenses'),
                    where('userId', '==', currentUser.uid),
                    where('date', '>=', startDate),
                    where('date', '<=', endDate)
                );

            const snapshot = await getDocs(expensesQuery);
            const transactions = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id
            }));

            if (transactions.length === 0) {
                setAnalysis({
                    categories: [],
                    monthlyTotal: 0,
                    averageExpense: 0,
                    stdDev: 0,
                    maxExpense: 0,
                    frequentDays: [],
                    recommendations: ['Nessuna transazione registrata per questo periodo.'],
                    trends: []
                });
                return;
            }

            const categoryData = {};
            let totalAmount = 0;
            let maxExpense = 0;
            const expenseAmounts = [];
            const dayCount = {};

            transactions.forEach((transaction) => {
                const category = transaction.category || 'Non specificata';
                const amount = transaction.amount || 0;
                const date = transaction.date.toDate ? transaction.date.toDate() : new Date(transaction.date);
                const dayOfWeek = format(date, 'EEEE', { locale: it });

                if (!categoryData[category]) {
                    categoryData[category] = { count: 0, amount: 0 };
                }
                categoryData[category].count += 1;
                categoryData[category].amount += amount;

                totalAmount += amount;
                expenseAmounts.push(amount);
                if (amount > maxExpense) {
                    maxExpense = amount;
                }

                dayCount[dayOfWeek] = (dayCount[dayOfWeek] || 0) + 1;
            });

            const averageExpense = totalAmount / transactions.length;
            const stdDev = calculateStdDev(expenseAmounts, averageExpense);

            const sortedCategories = Object.entries(categoryData)
                .sort((a, b) => b[1].amount - a[1].amount)
                .map(([category, data]) => ({
                    name: category,
                    amount: data.amount,
                    count: data.count,
                    percentage: (data.amount / totalAmount) * 100
                }));

            const frequentDays = Object.entries(dayCount)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([day, count]) => ({ day, count }));

            const recommendations = generateRecommendations(categoryData, totalAmount, transactions, averageExpense, stdDev, maxExpense);

            setAnalysis({
                categories: sortedCategories,
                monthlyTotal: totalAmount,
                averageExpense,
                stdDev,
                maxExpense,
                frequentDays,
                recommendations,
                trends: sortedCategories // Qui potresti estendere con ulteriori trend in futuro
            });

        } catch (error) {
            console.error('Errore nell\'analisi delle abitudini:', error);
            setError('Si è verificato un errore durante l\'analisi delle spese.');
        } finally {
            setLoading(false);
        }
    }, [userFamily, year, month]);


    useEffect(() => {
        setLoading(true);
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                analyzeHabits(user);
            } else {
                setError('Utente non autenticato');
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, [year, month, userFamily, analyzeHabits]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  return (
    <Paper sx={{ padding: 3, borderRadius: '12px', boxShadow: 2, backgroundColor: 'background.paper', maxHeight: '600px', overflowY: 'auto' }}> {/* Aggiunto maxHeight e overflowY */}
      {analysis.monthlyTotal > 0 ? (
        <>
          <Typography variant="h6" gutterBottom>
            Analisi delle Spese
          </Typography>

          <Typography variant="body1" gutterBottom>
            Spesa totale: €{analysis.monthlyTotal.toFixed(2)}
          </Typography>

          <Typography variant="body1" gutterBottom>
            Media per transazione: €{analysis.averageExpense.toFixed(2)}
          </Typography>

          <Typography variant="body2" gutterBottom>
            Deviazione standard: €{analysis.stdDev.toFixed(2)}
          </Typography>

          <Typography variant="body2" gutterBottom>
            Transazione più elevata: €{analysis.maxExpense.toFixed(2)}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Distribuzione delle Spese per Categoria
          </Typography>

          <List>
            {analysis.categories.map((category) => (
              <ListItem key={category.name}>
                <ListItemText
                  primary={`${category.name}: €${category.amount.toFixed(2)}`}
                  secondary={`${category.percentage.toFixed(1)}% del totale - ${category.count} transazioni`}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Suggerimenti
          </Typography>

          <List>
            {analysis.recommendations.map((recommendation, index) => (
              <ListItem key={index}>
                <ListItemText primary={recommendation} />
              </ListItem>
            ))}
          </List>

          {analysis.frequentDays.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1">
                Giorni con più transazioni:
                {analysis.frequentDays.map(({ day, count }, index) => (
                  <span key={day}>
                    {index > 0 ? ', ' : ' '}{day} ({count} transazioni)
                  </span>
                ))}
              </Typography>
            </>
          )}
        </>
      ) : (
        <Typography variant="body1">
          Nessuna transazione registrata per questo periodo.
        </Typography>
      )}
    </Paper>
  );
};

export default HabitAnalyzer;